import * as moment from 'moment-timezone';
import { AlertController, NavController, NavParams, Platform } from 'ionic-angular';
import { Api } from '../../../../providers/api';
import { BookingGroup } from '../../../../entities/booking-group';
import { Component } from '@angular/core';
import { DatePicker } from '@ionic-native/date-picker';
import { Port } from '../../../../entities/port';
import { PostSearchSailingsObject } from '../../../../interfaces/api';
import { Route } from '../../../../entities/route';
import { RoutePage } from './route/route';
import { Sailing } from '../../../../entities/sailing';
import { SearchableItem, SearchPage } from '../../../search/search';
import { Shared } from '../../../../providers/shared';
import { ShippingLine } from '../../../../entities/shipping-line';
import { SpinnerDialog } from '@ionic-native/spinner-dialog';
import { VehicleType } from '../../../../entities/vehicle-type';

interface SelectConfig {
    title: string,
}

class BookingValidator {
    missing_departure_port: boolean = true;
    missing_arrival_port: boolean = true;

    constructor(private bookings: BookingGroup) {
        this.missing_arrival_port = this.bookings.arrival_port === undefined;
        this.missing_departure_port = this.bookings.departure_port === undefined;
    }

    fails() {
        return this.missing_departure_port || this.missing_arrival_port;
    }
}

interface BookingErrors {
    departure_port: boolean;
    arrival_port: boolean;
    date: boolean;
}

type PortSelectionType = 'departure_port' | 'arrival_port';

type JourneyType = 'outbound' | 'return';

@Component({
    selector: 'page-create-booking',
    templateUrl: 'create-booking.html',
})
export class CreateBookingPage {
    bookings: BookingGroup;

    routes: Route[] = [];

    isOutboundToday: boolean = true;
    isOutboundTomorrow: boolean = false;
    isOutboundOtherDate: boolean = false;

    isReturnToday: boolean = false;
    isReturnTomorrow: boolean = true;
    isReturnOtherDate: boolean = false;

    operatorConfig: SelectConfig = {
        title: 'Available Operators',
    };
    vehicleTypeConfig: SelectConfig = {
        title: 'Vehicle',
    };

    vehicle_type_id: number = -1;

    shippingLines: ShippingLine[] = [];
    filteredShippingLines: ShippingLine[] = [];

    vehicleTypes: VehicleType[] = [];

    errors: BookingErrors = {
        departure_port: false,
        arrival_port: false,
        date: false,
    };

    selectedDepatureDate: string;
    selectedReturnDate: string;
    isMobile: boolean;
    minDate: string;

    constructor(private alertCtrl: AlertController, private api: Api, private datePicker: DatePicker, private navCtrl: NavController, private navParams: NavParams, private platform: Platform, private spinner: SpinnerDialog) {
        this.bookings = new BookingGroup();
        this.bookings.route = this.navParams.get('route');
        this.bookings.is_return_journey = false;

        if (this.bookings.route !== undefined) {
            this.setDeparturePort(this.bookings.route.getDeparturePort());
            this.setArrivalPort(this.bookings.route.getDestinationPort());
        }
        this.bookings.outbound_booking.departing_at = moment();
        this.bookings.return_booking.departing_at = moment().add(1, 'days');

        this.isMobile = this.platform.is('mobile');
        let today = new Date();
        this.minDate = moment(today).format('YYYY-MM-DDTHH:mm:ss');

        console.log(this.minDate, today);

        this.platform.ready().then(async () => {
            this.shippingLines = await this.api.getShippingLines();
            this.vehicleTypes = await this.api.getVehicleTypes();

            if (this.bookings.route) this.filteredShippingLines = this.bookings.route.shipping_lines;
            else this.filterShippingLines();
        });
    }

    changeVehicleType() {
        if (this.vehicle_type_id === undefined || this.vehicle_type_id === -1) {
            this.bookings.vehicle_type = undefined;
            return;
        }
        for (let vt of this.vehicleTypes) {
            if (vt.id === this.vehicle_type_id) {
                this.bookings.vehicle_type = vt;
                return;
            }
        }
    }

    portName(type: PortSelectionType): string {
        if (this.bookings[type] === undefined || this.bookings[type].name === undefined) return '';
        return this.bookings[type].name;
    }

    async search() {
        let validator: BookingValidator = this.bookingValidator();

        if (validator.fails()) {
            let alert = this.alertCtrl.create({
                title: 'Ports',
                subTitle: 'You must enter both departure and arrival ports.',
                buttons: ['Dismiss'],
            });
            alert.present();
            return;
        }
        this.spinner.show();

        let sailingsRequest: PostSearchSailingsObject = this.buildSailingsRequestObject();
        let sailings: Sailing[] = await this.api.postSailings(sailingsRequest);
        this.navCtrl.push(RoutePage, {
            bookings: this.bookings,
            sailings: sailings,
            vehicleTypes: this.vehicleTypes,
        });
        this.spinner.hide();
    }

    private buildSailingsRequestObject(): PostSearchSailingsObject {
        let sailingsRequest: PostSearchSailingsObject = {
            departure_port_id: this.bookings.departure_port.id,
            arrival_port_id: this.bookings.arrival_port.id,
            date: this.bookings.outbound_booking.departing_at.format('YYYY-MM-DD'),
        };

        if (this.bookings.shipping_line !== undefined) sailingsRequest.shipping_line_id = this.bookings.shipping_line.id;

        if (this.bookings.vehicle_type !== undefined) sailingsRequest.vehicle_type_id = this.bookings.vehicle_type.id;

        return sailingsRequest;
    }

    private bookingValidator(): BookingValidator {
        let validator: BookingValidator = new BookingValidator(this.bookings);
        this.errors.arrival_port = validator.missing_arrival_port;
        this.errors.departure_port = validator.missing_departure_port;
        return validator;
    }

    swapPortValues() {
        let departure_port = this.bookings.departure_port;
        this.bookings.departure_port = this.bookings.arrival_port;
        this.bookings.arrival_port = departure_port;
    }

    setReturnJourney(isReturn: boolean) {
        this.bookings.is_return_journey = isReturn;
    }

    setToday(type: JourneyType) {
        let bookingsKey: string = type + '_booking';
        let ucType: string = type.replace(/^(.)|\s+(.)/g, function ($1) {
            return $1.toUpperCase();
        });
        this['is' + ucType + 'OtherDate'] = false;
        this['is' + ucType + 'Tomorrow'] = false;
        this['is' + ucType + 'Today'] = true;
        this.bookings[bookingsKey].departing_at = moment();

        let outboundDeparture = this.bookings.outbound_booking.departing_at;
        let returnDeparture = this.bookings.return_booking.departing_at;

        if (outboundDeparture !== undefined && returnDeparture !== undefined) {
            outboundDeparture = outboundDeparture.startOf('day');
            returnDeparture = returnDeparture.startOf('day');
            if (moment.duration(returnDeparture.diff(outboundDeparture)).asSeconds() < 0) {
                returnDeparture = outboundDeparture;
                this.isReturnToday = this.isOutboundToday;
                this.isReturnTomorrow = this.isOutboundTomorrow;
                this.isReturnOtherDate = this.isOutboundOtherDate;
            }
            this.bookings.return_booking.departing_at = returnDeparture;
        }
    }

    setTomorrow(type: JourneyType) {
        let bookingsKey: string = type + '_booking';
        let ucType: string = type.replace(/^(.)|\s+(.)/g, function ($1) {
            return $1.toUpperCase();
        });
        this['is' + ucType + 'OtherDate'] = false;
        this['is' + ucType + 'Tomorrow'] = true;
        this['is' + ucType + 'Today'] = false;
        this.bookings[bookingsKey].departing_at = moment().add(1, 'days');

        let outboundDeparture = this.bookings.outbound_booking.departing_at;
        let returnDeparture = this.bookings.return_booking.departing_at;

        if (outboundDeparture !== undefined && returnDeparture !== undefined) {
            outboundDeparture = outboundDeparture.startOf('day');
            returnDeparture = returnDeparture.startOf('day');
            if (moment.duration(returnDeparture.diff(outboundDeparture)).asSeconds() < 0) {
                returnDeparture = outboundDeparture;
                this.isReturnToday = this.isOutboundToday;
                this.isReturnTomorrow = this.isOutboundTomorrow;
                this.isReturnOtherDate = this.isOutboundOtherDate;
            }
            this.bookings.return_booking.departing_at = returnDeparture;
        }
    }

    setOtherDate(type: JourneyType) {
        let bookingsKey: string = type + '_booking';
        let ucType: string = type.replace(/^(.)|\s+(.)/g, function ($1) {
            return $1.toUpperCase();
        });
        this.datePicker
            .show({
                allowOldDates: false,
                androidTheme: this.datePicker.ANDROID_THEMES.THEME_HOLO_DARK,
                date: this.bookings[bookingsKey].departing_at.toDate(),
                minDate: moment().toDate(),
                mode: 'date',
            })
            .then(
                (date) => {
                    if (date === undefined) return;

                    this.bookings[bookingsKey].departing_at = moment(date);
                    let returnDepartureForced: boolean = false;

                    let outboundDeparture = this.bookings.outbound_booking.departing_at;
                    let returnDeparture = this.bookings.return_booking.departing_at;

                    if (outboundDeparture !== undefined && returnDeparture !== undefined) {
                        outboundDeparture = outboundDeparture.startOf('day');
                        returnDeparture = returnDeparture.startOf('day');
                        if (moment.duration(returnDeparture.diff(outboundDeparture)).asSeconds() < 0) {
                            returnDeparture = outboundDeparture;
                            returnDepartureForced = true;
                        }
                        this.bookings.return_booking.departing_at = returnDeparture;
                    }

                    let today = moment().endOf('day');
                    let tomorrow = moment().add(1, 'days').endOf('day');

                    this['is' + ucType + 'OtherDate'] = false;
                    this['is' + ucType + 'Tomorrow'] = false;
                    this['is' + ucType + 'Today'] = false;

                    if (returnDepartureForced) {
                        this.isReturnOtherDate = false;
                        this.isReturnTomorrow = false;
                        this.isReturnToday = false;
                    }

                    if (moment.duration(this.bookings[bookingsKey].departing_at.diff(today)).asSeconds() <= 0) this['is' + ucType + 'Today'] = true;
                    else if (moment.duration(this.bookings[bookingsKey].departing_at.diff(tomorrow)).asSeconds() <= 0) this['is' + ucType + 'Tomorrow'] = true;
                    else this['is' + ucType + 'OtherDate'] = true;

                    if (returnDepartureForced) {
                        this.isReturnOtherDate = this.isOutboundOtherDate;
                        this.isReturnTomorrow = this.isOutboundTomorrow;
                        this.isReturnToday = this.isOutboundToday;
                    }
                },
                (err) => console.log('Error occurred while getting date: ', err)
            );
    }

    onDateChange(type: JourneyType, date: any) {

        date.month = date.month - 1;
        let bookingsKey: string = type + '_booking';
        let ucType: string = type.replace(/^(.)|\s+(.)/g, function ($1) {
            return $1.toUpperCase();
        });

        this.bookings[bookingsKey].departing_at = moment(date);

        let returnDepartureForced: boolean = false;

        let outboundDeparture = this.bookings.outbound_booking.departing_at;
        let returnDeparture = this.bookings.return_booking.departing_at;

        if (outboundDeparture !== undefined && returnDeparture !== undefined) {
            outboundDeparture = outboundDeparture.startOf('day');
            returnDeparture = returnDeparture.startOf('day');
            if (moment.duration(returnDeparture.diff(outboundDeparture)).asSeconds() < 0) {
                returnDeparture = outboundDeparture;
                returnDepartureForced = true;
            }
            this.bookings.return_booking.departing_at = returnDeparture;
        }

        let today = moment().endOf('day');
        let tomorrow = moment().add(1, 'days').endOf('day');

        this['is' + ucType + 'OtherDate'] = false;
        this['is' + ucType + 'Tomorrow'] = false;
        this['is' + ucType + 'Today'] = false;

        if (returnDepartureForced) {
            this.isReturnOtherDate = false;
            this.isReturnTomorrow = false;
            this.isReturnToday = false;
        }

        if (moment.duration(this.bookings[bookingsKey].departing_at.diff(today)).asSeconds() <= 0) this['is' + ucType + 'Today'] = true;
        else if (moment.duration(this.bookings[bookingsKey].departing_at.diff(tomorrow)).asSeconds() <= 0) this['is' + ucType + 'Tomorrow'] = true;
        else this['is' + ucType + 'OtherDate'] = true;

        if (returnDepartureForced) {
            this.isReturnOtherDate = this.isOutboundOtherDate;
            this.isReturnTomorrow = this.isOutboundTomorrow;
            this.isReturnToday = this.isOutboundToday;
        }
    }

    async loadPortSearch(type: PortSelectionType) {
        this.errors[type] = false;
        let ports: Port[] = [];

        this.spinner.show();
        if (type === 'departure_port' || this.bookings.departure_port === undefined) {
            ports = await this.api.getAllPorts();
        } else {
            this.routes = await this.api.getAllRoutesForPort(this.bookings.departure_port);
            for (let route of this.routes) {
                let port: Port = route.getDestinationPort();
                if (port === undefined) continue;
                ports.push(port);
            }

            if (ports.length < 1) {
                let alert = this.alertCtrl.create({
                    title: 'No Destinations Available',
                    subTitle: 'Sorry, there are no available destinations when departing from <b>' + this.bookings.departure_port.name + '</b>.',
                    buttons: ['Dismiss'],
                });
                alert.present();
                return;
            }
        }

        let items: SearchableItem[] = Shared.convertPortsToSearchableItems(ports);
        let title: string = 'Departure Port';
        let message: string = 'Choose a Departure Port';

        if (type === 'arrival_port') {
            title = 'Arrival Port';
            message = 'Choose an Arrival Port';
        }

        this.spinner.hide();
        this.navCtrl.push(SearchPage, {
            placeholder: 'Port name',
            items: items,
            done: async (item: SearchableItem) => {
                let port = Shared.convertItemToPort(item, ports);

                if (type == 'departure_port') this.setDeparturePort(port);
                if (type == 'arrival_port') this.setArrivalPort(port);

                this.navCtrl.pop();
            },
            query: (query) => {},
            message: message,
            title: title,
        });
    }

    public clearShippingLine() {
        this.setShippingLine(undefined);
    }

    public filterShippingLines() {
        let routes: Route[] = this.routes;
        let departurePort: Port = this.bookings.departure_port;
        let arrivalPort: Port = this.bookings.arrival_port;

        let shippingLines: ShippingLine[] = [];

        for (let route of routes) {
            let routeDeparturePort = route.getDeparturePort();
            let routeArrivalPort = route.getDestinationPort();

            if (!routeDeparturePort || !routeArrivalPort) continue;

            if (routeDeparturePort.abbr !== departurePort.abbr) continue;
            if (arrivalPort && routeArrivalPort.abbr !== arrivalPort.abbr) continue;

            for (let shippingLine of route.shipping_lines) {
                shippingLines.push(shippingLine);
            }
        }

        this.filteredShippingLines = shippingLines;
        this.resetShippingLineIfNecessary();
    }

    public getShippingLine(): ShippingLine {
        return this.bookings.shipping_line;
    }

    public resetShippingLineIfNecessary() {
        let currentShippingLine = this.getShippingLine();
        if (!currentShippingLine) return;

        for (let shippingLine of this.filteredShippingLines) {
            if (shippingLine.id == currentShippingLine.id) return;
        }

        this.clearShippingLine();
    }

    public setArrivalPort(port: Port) {
        this.bookings.arrival_port = port;
        this.filterShippingLines();
    }

    public setDeparturePort(port: Port) {
        this.bookings.departure_port = port;
        this.setArrivalPort(undefined);
    }

    public setShippingLine(shippingLine: ShippingLine) {
        this.bookings.shipping_line = shippingLine;
    }

    public get shipping_line_id(): number {
        let shippingLine = this.getShippingLine();
        if (shippingLine == undefined) return -1;
        return shippingLine.id;
    }

    public set shipping_line_id(id: number) {
        for (let shippingLine of this.shippingLines) {
            if (shippingLine.id == id) {
                this.setShippingLine(shippingLine);
                return;
            }
        }
        this.clearShippingLine();
    }
}



// WEBPACK FOOTER //
// ./src/pages/bookings/my-bookings/create-booking/create-booking.ts